import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon, UtensilsIcon, ActivityIcon } from 'lucide-react';
const TurkeyTail = () => {
  const [activeSection, setActiveSection] = useState('bioenergetics');
  return <div className="w-full bg-white" data-id="element-1638">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-orange-50 to-orange-100" data-id="element-1639">
        <div className="max-w-4xl mx-auto" data-id="element-1640">
          <div className="w-20 h-1 bg-orange-500 mx-auto mb-8 rounded-full" data-id="element-1641"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-1642">
            Health — Turkey Tail
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-1643">
            Immune support with a light, refreshing taste.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-1644">
        <div className="container mx-auto" data-id="element-1645">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1646">
            Turkey Tail Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-1647">
            Our proprietary extraction technology unlocks the full spectrum of
            Turkey Tail bioactives, delivering nine distinct benefit categories
            in a single cup.
          </p>
          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-1648">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-1649">
              Turkey Tail Spora | Prebiotic Vitality + Immunonutrients +
              Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-1650">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-1651">
              <nav className="flex flex-col space-y-4" data-id="element-1652">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'bioenergetics' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('bioenergetics')} data-id="element-1653">
                  <div className="flex items-center gap-3" data-id="element-1654">
                    <UtensilsIcon className="w-5 h-5" data-id="element-1655" />
                    <span data-id="element-1656">Prebiotic Vitality</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-1657">
                  <div className="flex items-center gap-3" data-id="element-1658">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-1659" />
                    <span data-id="element-1660">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-1661">
                  <div className="flex items-center gap-3" data-id="element-1662">
                    <ShieldIcon className="w-5 h-5" data-id="element-1663" />
                    <span data-id="element-1664">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-1665">
              {/* Prebiotic Vitality Section */}
              <section id="bioenergetics" className={`pb-24 ${activeSection === 'bioenergetics' ? 'block' : 'hidden'}`} data-id="element-1666">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1667">
                  The Prebiotic Vitality of Turkey Tail
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1668">
                  Turkey Tail enhances energy production, supports circulatory
                  health through gut-mediated metabolites, and improves
                  respiratory health through enhanced mucosal immune defense.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1669">
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1670">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1671">
                      <ZapIcon className="w-6 h-6" data-id="element-1672" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1673">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1674">
                      Baseline vitality
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1675">
                      <span className="font-medium" data-id="element-1676">Experience:</span>{' '}
                      Consistent energy levels and improved stamina.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1677">
                      <span className="font-medium" data-id="element-1678">Why it works:</span>{' '}
                      Polysaccharides support mitochondrial activity for better
                      energy production.
                    </p>
                    <div className="mt-auto" data-id="element-1679">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1680">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1681">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1682">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1683">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1684">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1685" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1686">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1687">
                      Whole-body wellness
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1688">
                      <span className="font-medium" data-id="element-1689">Experience:</span> Improved
                      vascular health and blood pressure support.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1690">
                      <span className="font-medium" data-id="element-1691">Why it works:</span>{' '}
                      Gut-mediated metabolites provide indirect vascular support
                      through microbiome balance.
                    </p>
                    <div className="mt-auto" data-id="element-1692">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1693">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1694">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1695">
                          Gut-mediated metabolites
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1696">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1697">
                      <div className="w-6 h-6" data-id="element-1698" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1699">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1700">
                      Seasonal resilience
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1701">
                      <span className="font-medium" data-id="element-1702">Experience:</span> Better
                      resistance to seasonal illness and respiratory challenges.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1703">
                      <span className="font-medium" data-id="element-1704">Why it works:</span>{' '}
                      β-glucans enhance mucosal immune defense in the
                      respiratory tract.
                    </p>
                    <div className="mt-auto" data-id="element-1705">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1706">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1707">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1708">
                          β-glucans
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-1709">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1710">The Immune Matrix</h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1711">
                  Turkey Tail contains unique compounds that enhance immune
                  system activity, buffer stress, and support metabolic
                  stability through the gut-immune link.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1712">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1713">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1714">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-1715" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1716">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1717">
                      Enhanced defense
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1718">
                      <span className="font-medium" data-id="element-1719">Experience:</span> Improved
                      defense and resilience against illness.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1720">
                      <span className="font-medium" data-id="element-1721">Why it works:</span>{' '}
                      β-glucans, PSP, and PSK enhance immune system activity.
                    </p>
                    <div className="mt-auto" data-id="element-1722">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1723">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1724">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1725">
                          β-glucans
                        </span>
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1726">
                          PSP
                        </span>
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1727">
                          PSK
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1728">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1729">
                      <CircleIcon className="w-6 h-6" data-id="element-1730" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1731">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1732">
                      Balanced response
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1733">
                      <span className="font-medium" data-id="element-1734">Experience:</span> Daily
                      balance without overstimulation.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1735">
                      <span className="font-medium" data-id="element-1736">Why it works:</span>{' '}
                      Polysaccharides buffer stress and support systemic
                      balance.
                    </p>
                    <div className="mt-auto" data-id="element-1737">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1738">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1739">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1740">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Endocrine Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1741">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1742">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-1743" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1744">
                      Endocrine Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1745">
                      Metabolic stability
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1746">
                      <span className="font-medium" data-id="element-1747">Experience:</span> Better
                      metabolic stability through gut-immune link.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1748">
                      <span className="font-medium" data-id="element-1749">Why it works:</span>{' '}
                      Prebiotic fibers modulate the gut-hormone axis.
                    </p>
                    <div className="mt-auto" data-id="element-1750">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1751">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1752">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1753">
                          Prebiotic fibers
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-1754">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1755">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1756">
                  Turkey Tail contains powerful compounds that defend cells from
                  oxidative damage, reduce inflammation, and support cognitive
                  clarity under stress.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1757">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1758">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1759">
                      <ShieldIcon className="w-6 h-6" data-id="element-1760" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1761">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1762">
                      Cellular defense
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1763">
                      <span className="font-medium" data-id="element-1764">Experience:</span> Long-term
                      cellular health and vitality.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1765">
                      <span className="font-medium" data-id="element-1766">Why it works:</span>{' '}
                      Phenolic acids and flavonoids defend cells from oxidative
                      damage.
                    </p>
                    <div className="mt-auto" data-id="element-1767">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1768">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1769">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1770">
                          Phenolic acids
                        </span>
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1771">
                          Flavonoids
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1772">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1773">
                      <ActivityIcon className="w-6 h-6" data-id="element-1774" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1775">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1776">
                      Reduced inflammation
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1777">
                      <span className="font-medium" data-id="element-1778">Experience:</span> Lower
                      chronic inflammation and healthier recovery.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1779">
                      <span className="font-medium" data-id="element-1780">Why it works:</span> Sterols
                      and PSK reduce inflammatory cytokines.
                    </p>
                    <div className="mt-auto" data-id="element-1781">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1782">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1783">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1784">
                          Sterols
                        </span>
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1785">
                          PSK
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Neuroprotective Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1786">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1787">
                      <BrainIcon className="w-6 h-6" data-id="element-1788" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1789">Neuroprotective</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1790">
                      Cognitive clarity
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1791">
                      <span className="font-medium" data-id="element-1792">Experience:</span> Support
                      for cognitive clarity under stress.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1793">
                      <span className="font-medium" data-id="element-1794">Why it works:</span>{' '}
                      Polysaccharopeptides regulate cytokine activity in the
                      brain.
                    </p>
                    <div className="mt-auto" data-id="element-1795">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1796">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1797">
                        <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1798">
                          Polysaccharopeptides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-1799">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-1800">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-1801">
                Turkey Tail Spora | Prebiotic Vitality + Immunonutrients +
                Antioxidants
              </p>
            </div>
            <div data-id="element-1802">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'bioenergetics' ? '' : 'bioenergetics')} data-id="element-1803">
                <div className="flex items-center gap-3" data-id="element-1804">
                  <UtensilsIcon className="w-5 h-5" data-id="element-1805" />
                  <h2 className="text-xl font-bold" data-id="element-1806">Prebiotic Vitality</h2>
                </div>
                <span data-id="element-1807">{activeSection === 'bioenergetics' ? '−' : '+'}</span>
              </button>
              {activeSection === 'bioenergetics' && <div className="p-4 space-y-8" data-id="element-1808">
                  {/* Mobile content for Prebiotic Vitality section */}
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-1809">
                    <div className="flex items-center gap-4 mb-3" data-id="element-1810">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-1811">
                        <ZapIcon className="w-5 h-5" data-id="element-1812" />
                      </div>
                      <div data-id="element-1813">
                        <h3 className="font-bold" data-id="element-1814">Bioenergetics</h3>
                        <p className="text-xs text-gray-500" data-id="element-1815">
                          Baseline vitality
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1816">
                      <span className="font-medium" data-id="element-1817">Experience:</span>{' '}
                      Consistent energy levels and improved stamina.
                    </p>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1818">
                      <span className="font-medium" data-id="element-1819">Why it works:</span>{' '}
                      Polysaccharides support mitochondrial activity for better
                      energy production.
                    </p>
                    <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-1820">
                      Key Compounds:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-1821">
                      <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium" data-id="element-1822">
                        Polysaccharides
                      </span>
                    </div>
                  </div>
                  {/* Add the other mobile cards for this section */}
                </div>}
            </div>
            <div data-id="element-1823">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-1824">
                <div className="flex items-center gap-3" data-id="element-1825">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-1826" />
                  <h2 className="text-xl font-bold" data-id="element-1827">The Immune Matrix</h2>
                </div>
                <span data-id="element-1828">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-1829">
                  {/* Mobile content for Immunonutrients section */}
                </div>}
            </div>
            <div data-id="element-1830">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-1831">
                <div className="flex items-center gap-3" data-id="element-1832">
                  <ShieldIcon className="w-5 h-5" data-id="element-1833" />
                  <h2 className="text-xl font-bold" data-id="element-1834">The Protection Network</h2>
                </div>
                <span data-id="element-1835">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-1836">
                  {/* Mobile content for Antioxidants section */}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-1837">
        <div className="max-w-4xl mx-auto" data-id="element-1838">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1839">
            Experience Turkey Tail Spora
          </h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-1840">
            Immune support with a light, refreshing taste that fortifies your
            body's natural defenses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1841">
            <Link to="/#products" className="bg-orange-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-orange-600 transition-colors" data-id="element-1842">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-1843">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-1844">
              <span data-id="element-1845">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1846" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default TurkeyTail;